import { CloudUploadOutlined } from '@ant-design/icons';
import { Button, Radio, Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { ChangeEventHandler, useEffect, useState } from 'react';

import useReduxState from '@portal/hooks/useReduxState';
import { onboarding } from '@portal/config/app.json';
import { DocumentType } from '@portal/models/enumerators/onboarding';
import { translate } from '@portal/services/i18n';

interface PendingDocumentsUploadModalGeneralProps {
  onFileChange: ChangeEventHandler<HTMLInputElement>;
  modal: boolean;
  onUpload: () => void;
  onCancel: () => void;
  file: File | null;
}

interface SpecificDoubleDocumentProps
  extends PendingDocumentsUploadModalGeneralProps {
  chooseDocument?: undefined;
  uniquePhoto?: undefined;
  documentType: DocumentType.RG | DocumentType.CNH;
  setDocumentType?: undefined;
  photoDocumentType?: undefined;
  onSecondFileChange: ChangeEventHandler<HTMLInputElement>;
  secondFile: File | null;
  pendingDocumentName?: string;
}

interface PhotoDocumentProps extends PendingDocumentsUploadModalGeneralProps {
  chooseDocument: true;
  uniquePhoto?: undefined;
  documentType: DocumentType.PHOTO_DOCUMENT;
  setDocumentType: (value: number) => void;
  photoDocumentType:
    | DocumentType.RG
    | DocumentType.CNH
    | DocumentType.DIGITAL_CNH;
  onSecondFileChange: ChangeEventHandler<HTMLInputElement>;
  secondFile: File | null;
  pendingDocumentName?: string;
}

interface SpecificDocumentProps
  extends PendingDocumentsUploadModalGeneralProps {
  chooseDocument?: undefined;
  setDocumentType?: undefined;
  uniquePhoto: true;
  documentType:
    | DocumentType.RG_BACK
    | DocumentType.RG_FRONT
    | DocumentType.CNH_FRONT
    | DocumentType.CNH_BACK
    | DocumentType.SELFIE
    | DocumentType.POWER_OF_ATTORNEY
    | DocumentType.CNPJ_PROOF
    | DocumentType.ADDRESS_PROOF
    | DocumentType.PHOTO_DOCUMENT
    | DocumentType.CUSTOM
    | DocumentType.SOCIAL_CONTRACT
    | DocumentType.COMPANY_STATUTE
    | DocumentType.DECLARATION_OF_BENEFICIARIES
    | DocumentType.PROOF_OF_INVOICING;

  photoDocumentType?: undefined;
  onSecondFileChange?: undefined;
  secondFile?: undefined;
  pendingDocumentName?: string;
}

const PendingDocumentsUploadModal = ({
  chooseDocument,
  uniquePhoto,
  documentType,
  setDocumentType,
  photoDocumentType,
  modal,
  onUpload,
  onCancel,
  file,
  secondFile,
  onFileChange,
  onSecondFileChange,
  pendingDocumentName,
}:
  | SpecificDoubleDocumentProps
  | SpecificDocumentProps
  | PhotoDocumentProps) => {
  const [preview, setPreview] = useState<string | undefined>();
  const [previewSecond, setPreviewSecond] = useState<string | undefined>();

  const { loadUploadImages } = useReduxState().loading;

  const isDisabled = React.useMemo(() => {
    if (!preview) {
      return true;
    }

    if (
      photoDocumentType === DocumentType.RG ||
      documentType === DocumentType.RG
    ) {
      return !previewSecond;
    }

    return false;
  }, [documentType, photoDocumentType, preview, previewSecond]);

  useEffect(() => {
    if (!file) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    if (!secondFile) {
      setPreviewSecond(undefined);
      return;
    }

    const secondObjectUrl = URL.createObjectURL(secondFile);
    setPreviewSecond(secondObjectUrl);

    return () => {
      URL.revokeObjectURL(secondObjectUrl);
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, secondFile]);

  const handleCloseModal = () => {
    onCancel();
  };

  const renderUniqueUpload = (isSecond = false) => (
    <div className="pending-document-modal__input">
      {(isSecond && !secondFile?.name) || (!isSecond && !file?.name) ? (
        <div className="pending-document-modal__input__container">
          <label htmlFor={`file-upload-${documentType}-${isSecond}`}>
            {translate('PAGES.PENDING_DOCUMENTS.MODAL.CHOOSE_FILE')}
          </label>
          <div className="pending-document-modal__title">
            {onboarding.maxFileSizeImage.enabled ? (
              <p>
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_IMAGE'
                )}
                {onboarding.maxFileSizeImage.sizeInMb}
                {translate('SHARED.MB')}
              </p>
            ) : null}
            {onboarding.maxFileSizePdf.enabled ? (
              <p>
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.UPLOAD_MAX_PDF'
                )}
                {onboarding.maxFileSizePdf.sizeInMb}
                {translate('SHARED.MB')}
              </p>
            ) : null}
          </div>
          <input
            className="pending-document-modal__input"
            type="file"
            name={`file-upload-${documentType}-${isSecond}`}
            id={`file-upload-${documentType}-${isSecond}`}
            title="input"
            onChange={
              isSecond && onSecondFileChange ? onSecondFileChange : onFileChange
            }
            accept={
              [DocumentType.POWER_OF_ATTORNEY].includes(documentType)
                ? 'application/pdf'
                : 'image/png, image/jpeg, application/pdf'
            }
          />
        </div>
      ) : (
        <div>
          <div className="pending-document-modal__preview__container">
            <span>
              {file && !isSecond ? (
                <>
                  {file.name?.endsWith('pdf') ? (
                    <iframe
                      src={`${preview}#toolbar=0`}
                      width="100%"
                      height={400}
                    />
                  ) : (
                    <img
                      className="pending-document-modal__preview__container__image"
                      src={preview}
                      alt="uploaded image"
                    />
                  )}
                </>
              ) : null}

              {secondFile && isSecond ? (
                <img
                  className="pending-document-modal__preview__container__image"
                  src={previewSecond}
                  alt="second uploaded image"
                />
              ) : null}
            </span>

            <label htmlFor={`file-upload-${documentType}-${isSecond}-again`}>
              <p className="pending-document-modal__preview__container__upload">
                {translate('PAGES.PENDING_DOCUMENTS.MODAL.CHOOSE_ANOTHER')}
              </p>
            </label>

            <input
              className="pending-document-modal__input"
              type="file"
              name={`file-upload-${documentType}-${isSecond}-again`}
              id={`file-upload-${documentType}-${isSecond}-again`}
              title="input"
              onChange={
                isSecond && onSecondFileChange
                  ? onSecondFileChange
                  : onFileChange
              }
              accept={
                [DocumentType.POWER_OF_ATTORNEY].includes(documentType)
                  ? '.application/pdf'
                  : 'image/png, image/jpeg, application/pdf'
              }
            />
          </div>
        </div>
      )}
    </div>
  );

  const useTwoPictures =
    (documentType === DocumentType.PHOTO_DOCUMENT &&
      (photoDocumentType === DocumentType.RG || onboarding.driversLicense)) ||
    (documentType === DocumentType.CNH && onboarding.driversLicense) ||
    documentType === DocumentType.RG;

  return (
    <Modal
      open={modal}
      footer={null}
      closable={true}
      onCancel={() => handleCloseModal()}
      className="pending-document-modal__message-modal"
    >
      <p className="pending-document-modal__message-modal__title">
        {documentType === DocumentType.SOCIAL_CONTRACT ||
        documentType === DocumentType.COMPANY_STATUTE ||
        documentType === DocumentType.DECLARATION_OF_BENEFICIARIES
          ? translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.FORM.BUTTON_FILE'
            )
          : translate('PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.FORM.BUTTON')}
      </p>
      {uniquePhoto ? (
        <p className="pending-document-modal__message-modal__subtitle">
          {documentType === DocumentType.CUSTOM
            ? pendingDocumentName
            : translate(
                `PAGES.PENDING_DOCUMENTS.DOCUMENTS.${DocumentType[documentType]}`
              )}
        </p>
      ) : null}

      {chooseDocument && setDocumentType ? (
        <Col span={24} className="pending-document-modal__radio__container">
          <Radio.Group
            name="documentType"
            defaultValue={DocumentType.CNH}
            value={photoDocumentType}
          >
            <Radio
              value={DocumentType.CNH}
              onClick={() => setDocumentType(DocumentType.CNH)}
              className="new-contact-modal__form__radio-label"
            >
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.CNH'
              )}
            </Radio>
            {onboarding.digitalDriverLicense ? (
              <Radio
                value={DocumentType.DIGITAL_CNH}
                onClick={() => setDocumentType(DocumentType.DIGITAL_CNH)}
                className="new-contact-modal__form__radio-label"
              >
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.DIGITAL_CNH'
                )}
              </Radio>
            ) : null}
            <Radio
              value={DocumentType.RG}
              onClick={() => setDocumentType(DocumentType.RG)}
              className="new-contact-modal__form__radio-label"
            >
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.DOCUMENT_TYPE.RG'
              )}
            </Radio>
          </Radio.Group>
        </Col>
      ) : null}
      <Col className="pending-document-modal__inputs-container">
        {uniquePhoto ? (
          renderUniqueUpload()
        ) : (
          <div>
            <p className="pending-document-modal__inputs-container__text">
              {photoDocumentType === DocumentType.DIGITAL_CNH ? (
                <>
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.CNH.DIGITAL'
                  )}
                </>
              ) : (
                <>
                  {translate(
                    `PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.${
                      DocumentType[photoDocumentType ?? documentType]
                    }.${
                      photoDocumentType || documentType === DocumentType.RG
                        ? 'FRONT'
                        : 'OPEN'
                    }`
                  )}
                </>
              )}
            </p>
            {renderUniqueUpload()}
            {useTwoPictures &&
            photoDocumentType !== DocumentType.DIGITAL_CNH ? (
              <>
                <p className="pending-document-modal__inputs-container__text">
                  {translate(
                    `PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.FORM.LABELS.${
                      DocumentType[photoDocumentType ?? documentType]
                    }.BACK`
                  )}
                </p>
                {renderUniqueUpload(true)}
              </>
            ) : null}
          </div>
        )}
      </Col>
      <Row
        className="pending-document-modal__message-modal__button"
        justify="center"
      >
        <Button
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          icon={<CloudUploadOutlined />}
          type="primary"
          disabled={isDisabled}
          onClick={onUpload}
          loading={loadUploadImages}
        >
          {translate('PAGES.PENDING_DOCUMENTS.MODAL.SUBMIT')}
        </Button>
      </Row>
    </Modal>
  );
};

export default PendingDocumentsUploadModal;
